import { api } from './api';

/**
 * Client for `GET /v1/conversations` — the freshest-first list of conversations
 * the calling device is a member of. Backed by
 * `services/api/internal/conversations/service.go` and the
 * `messaging.conversations` projection.
 *
 * The endpoint paginates with an opaque cursor (the BE encodes a
 * `last_event_at` timestamp internally — clients MUST treat it as opaque). A
 * freshly-online device calls `listConversations()` once with no cursor to
 * enumerate its chats, then per-conversation `GET /v1/messages?conv=&cursor=`
 * fills in the message timeline. The new live-update path is WS
 * `convs.refresh` (services/ws-fanout/src/stream.rs) — no polling needed.
 *
 * IDs come over the wire as base64url. The shared types use that form
 * verbatim so hosts don't have to round-trip through hex unless they want
 * to (the `to{Hex,Bytes}ConversationId` helpers below do that on demand).
 */
export interface ConversationWire {
  conversation_id: string;
  kind: 'direct' | 'group' | 'space' | string;
  workspace_id?: string;
  current_epoch: number;
  member_leaf_count: number;
  /** RFC 3339 UTC. */
  created_at: string;
  /** RFC 3339 UTC — freshest envelope or membership change. */
  last_event_at: string;
  /** Membership version (`mseq`) — present on DELTA rows only. See
   *  docs/architecture/membership-sync.md. */
  version?: number;
  /** Delta change kind: 'added' (seed/refresh) or 'removed' (hide — the device
   *  left / was removed). Present on DELTA rows only. */
  op?: 'added' | 'removed' | string;
}

export interface ListConversationsResponse {
  /** Cold-start (no `since`): the full paginated device membership list. */
  conversations?: ConversationWire[];
  /** Delta (`?since=v`): only the memberships added after `v`. */
  changes?: ConversationWire[];
  next_cursor?: string;
  /** The device's current membership version (`MAX(mseq)`) — always present on a
   *  server that supports delta sync; `undefined` from an older backend. */
  current_version?: number;
}

export interface ListConversationsOptions {
  /** 1..256, default 64 server-side. */
  limit?: number;
  /** Opaque next_cursor from a previous page. */
  cursor?: string;
  /** Membership version to fetch changes AFTER — switches the endpoint to DELTA
   *  mode (returns `changes` + `current_version`, O(changes)). */
  since?: number;
  signal?: AbortSignal;
}

export async function listConversations(
  opts: ListConversationsOptions = {},
): Promise<ListConversationsResponse> {
  const qs = new URLSearchParams();
  if (opts.limit !== undefined) qs.set('limit', String(opts.limit));
  if (opts.cursor !== undefined) qs.set('cursor', opts.cursor);
  if (opts.since !== undefined) qs.set('since', String(opts.since));
  const path = `/v1/conversations${qs.toString() ? `?${qs.toString()}` : ''}`;
  const reqOpts = opts.signal ? { signal: opts.signal } : {};
  return api.get<ListConversationsResponse>(path, reqOpts);
}

/** Result of a full membership walk: the rows + the version the caller is caught
 *  up to once applied, and whether every page was fetched (not truncated). */
export interface ListAllConversationsResult {
  conversations: ConversationWire[];
  /** The device's current membership version, or `undefined` on a legacy backend
   *  that doesn't return `current_version` (caller then stays on full polling). */
  currentVersion: number | undefined;
  /** True when the walk reached the end (no truncation) — only then is
   *  `currentVersion` safe to store as the client's cursor. */
  complete: boolean;
}

/** One DELTA fetch (paginates internally until the changes run out). Returns only
 *  the conversations joined after `since`, plus the new `currentVersion`. */
export interface MembershipDeltaResult {
  changes: ConversationWire[];
  /** The server's current membership version. If this is BELOW the `since` the
   *  caller passed, the backend was reset (its sequence went backwards) and the
   *  caller must full-resync. `undefined` on a legacy backend. */
  currentVersion: number | undefined;
}

function hexToBase64Url(hex: string): string {
  const clean = hex.length % 2 === 0 ? hex : hex.slice(0, hex.length - 1);
  const bytes = new Uint8Array(clean.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  }
  const B = (globalThis as { Buffer?: { from(a: Uint8Array): { toString(enc: string): string } } }).Buffer;
  let b64: string;
  if (B !== undefined) {
    b64 = B.from(bytes).toString('base64');
  } else {
    let bin = '';
    for (const byte of bytes) bin += String.fromCharCode(byte);
    b64 = btoa(bin);
  }
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Server-authoritative LEAVE: tell the backend this device departed the
 *  conversation. The backend marks the membership left + advances the version, so
 *  the account's other devices learn of the departure via an `op=removed` delta.
 *  Best-effort — the client already hides the chat locally + mirrors via the
 *  device-group `conv_state`; this adds server-side consistency. Idempotent. */
export async function leaveConversationOnServer(
  conversationIdHex: string,
  opts: { signal?: AbortSignal } = {},
): Promise<{ conversation_id: string; left: boolean }> {
  const b64 = hexToBase64Url(conversationIdHex);
  return api.post(
    `/v1/conversations/${encodeURIComponent(b64)}/leave`,
    undefined,
    opts.signal ? { signal: opts.signal } : {},
  );
}

/** Server-authoritative ADMIN REMOVE: tell the backend that `accountIds` were
 *  evicted from the conversation, so it stamps their `left_epoch` and `/members`
 *  stops returning them. The MLS Remove Commit is opaque to the server, so
 *  without this call the server roster keeps listing removed members forever and
 *  every device's authoritative reconcile re-adds them. Best-effort + idempotent
 *  — the client already patches its local roster + broadcasts `com.ping.kick`;
 *  this makes the departure consistent across ALL members. Requires the caller to
 *  be an admin/owner (the backend authorizes). Returns the count actually removed.
 */
export async function removeConversationMembersOnServer(
  conversationIdHex: string,
  accountIds: readonly string[],
  opts: { signal?: AbortSignal } = {},
): Promise<{ conversation_id: string; removed: number }> {
  const b64 = hexToBase64Url(conversationIdHex);
  return api.post(
    `/v1/conversations/${encodeURIComponent(b64)}/members/remove`,
    { account_ids: [...accountIds] },
    opts.signal ? { signal: opts.signal } : {},
  );
}

/** Fetch membership changes after `since`, following the version cursor across
 *  pages (the endpoint caps at 256 per call) until a short page. */
export async function listMembershipDelta(
  since: number,
  options: { pageSize?: number; maxPages?: number; signal?: AbortSignal } = {},
): Promise<MembershipDeltaResult> {
  const pageSize = options.pageSize ?? 256;
  const maxPages = options.maxPages ?? 64;
  const changes: ConversationWire[] = [];
  let cursor = since;
  let currentVersion: number | undefined;
  for (let i = 0; i < maxPages; i++) {
    const page = await listConversations({
      since: cursor,
      limit: pageSize,
      ...(options.signal ? { signal: options.signal } : {}),
    });
    currentVersion = page.current_version;
    const rows = page.changes ?? [];
    changes.push(...rows);
    if (rows.length < pageSize) break;
    // Advance the cursor to the highest version seen; rows are version-ordered.
    const last = rows[rows.length - 1];
    if (last?.version === undefined || last.version <= cursor) break;
    cursor = last.version;
  }
  return { changes, currentVersion };
}

/**
 * Walks the cursor pages until the server stops returning `next_cursor` and
 * returns the concatenated list PLUS the membership version the caller is caught
 * up to. This is the COLD-START full snapshot; steady-state sync uses
 * `listMembershipDelta`. Bounded by `maxPages` so a runaway server can't pin us
 * in a loop — default 16 pages × 64 = 1024 conversations is far above realistic
 * chat fleet sizes. `complete` is false if the walk was truncated at `maxPages`,
 * in which case `currentVersion` must NOT be stored as the cursor (we haven't
 * seen every conversation, so a later delta would miss the untruncated tail).
 */
export async function listAllConversations(
  options: { pageSize?: number; maxPages?: number; signal?: AbortSignal } = {},
): Promise<ListAllConversationsResult> {
  const pageSize = options.pageSize ?? 64;
  const maxPages = options.maxPages ?? 16;
  const all: ConversationWire[] = [];
  let cursor: string | undefined;
  let currentVersion: number | undefined;
  for (let i = 0; i < maxPages; i++) {
    const page = await listConversations({
      limit: pageSize,
      ...(cursor !== undefined ? { cursor } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
    // `current_version` is the global MAX(mseq) — identical on every page.
    if (page.current_version !== undefined) currentVersion = page.current_version;
    all.push(...(page.conversations ?? []));
    if (!page.next_cursor) return { conversations: all, currentVersion, complete: true };
    cursor = page.next_cursor;
  }
  return { conversations: all, currentVersion, complete: false };
}
